import { Component } from 'react';
import {
    Button,
    Dialog,
    Heading,
    Pane,
    Paragraph,
    Spinner,
    Text,
    TickCircleIcon,
    CrossIcon,
    toaster
} from 'evergreen-ui';
import Config from '../../../util/Configuration';
import Cookie from 'react-cookies';

class HeoSyncModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            isSyncing: false,
            progress: null,
            error: null
        };

        this.abortController = null;
    }

    componentWillUnmount() {
        this.stopSync();
    }

    stopSync = () => {
        if (this.abortController) {
            this.abortController.abort();
            this.abortController = null;
        }
    };

    startSync = async () => {
        this.setState({
            isSyncing: true,
            progress: { type: 'start', message: 'Inițializare sincronizare...' },
            error: null
        });

        this.abortController = new AbortController();
        
        try {
            const token = Cookie.load('token');
            const response = await fetch(`${Config.host}/v2/catalog/sync/heo`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'text/event-stream'
                },
                signal: this.abortController.signal
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';

            while (true) {
                const { done, value } = await reader.read();
                
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            this.setState({ progress: data });

                            if (data.type === 'complete') {
                                this.setState({ isSyncing: false });
                                toaster.success('Sincronizare completă!');
                            }

                            if (data.type === 'error') {
                                this.setState({ isSyncing: false, error: data.message });
                                toaster.danger('Eroare la sincronizare');
                            }
                        } catch (e) {
                            console.error('Error parsing SSE data:', e);
                        }
                    }
                }
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Sync error:', error);
                this.setState({
                    isSyncing: false,
                    error: error.message || 'Eroare la sincronizare'
                });
                toaster.danger('Eroare la sincronizare');
            }
        }
    };

    handleClose = () => {
        if (this.state.isSyncing) {
            this.stopSync();
        }
        this.setState({
            isSyncing: false,
            progress: null,
            error: null
        });
        this.props.onClose();
    };

    renderProgress() {
        const { progress, error } = this.state;

        if (error) {
            return (
                <Pane
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    padding={20}
                    backgroundColor="#fef2f2"
                    borderRadius={8}
                >
                    <CrossIcon color="danger" size={40} marginBottom={10} />
                    <Text color="danger">{error}</Text>
                </Pane>
            );
        }

        if (!progress) {
            return (
                <Pane textAlign="center" padding={20}>
                    <Paragraph>
                        Această operație va sincroniza catalogul cu datele din HEO.
                        Catalogul HEO conține ~90MB de date și sincronizarea poate dura câteva minute.
                    </Paragraph>
                </Pane>
            );
        }

        if (progress.type === 'complete') {
            return (
                <Pane
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    padding={20}
                    backgroundColor="#f0fdf4"
                    borderRadius={8}
                >
                    <TickCircleIcon color="success" size={40} marginBottom={10} />
                    <Heading size={500} marginBottom={10}>Sincronizare completă!</Heading>
                    <Pane display="grid" gridTemplateColumns="1fr 1fr" gap={10} width="100%">
                        <Pane textAlign="center" padding={10} backgroundColor="white" borderRadius={4}>
                            <Text display="block" size={600} fontWeight="bold" color="green">
                                {progress.created}
                            </Text>
                            <Text size={300} color="muted">Create</Text>
                        </Pane>
                        <Pane textAlign="center" padding={10} backgroundColor="white" borderRadius={4}>
                            <Text display="block" size={600} fontWeight="bold" color="blue">
                                {progress.updated}
                            </Text>
                            <Text size={300} color="muted">Actualizate</Text>
                        </Pane>
                        <Pane textAlign="center" padding={10} backgroundColor="white" borderRadius={4}>
                            <Text display="block" size={600} fontWeight="bold" color="neutral">
                                {progress.skipped}
                            </Text>
                            <Text size={300} color="muted">Sărite</Text>
                        </Pane>
                        <Pane textAlign="center" padding={10} backgroundColor="white" borderRadius={4}>
                            <Text display="block" size={600} fontWeight="bold" color="red">
                                {progress.errors}
                            </Text>
                            <Text size={300} color="muted">Erori</Text>
                        </Pane>
                    </Pane>
                    <Text marginTop={10} color="muted">
                        Durată: {progress.duration?.toFixed(1)}s
                    </Text>
                </Pane>
            );
        }

        // Progress state
        const processed = progress.processed || 0;

        return (
            <Pane padding={20}>
                <Pane display="flex" alignItems="center" marginBottom={20}>
                    <Spinner size={24} marginRight={10} />
                    <Text>{progress.message}</Text>
                </Pane>

                {processed > 0 && (
                    <>
                        <Pane
                            height={8}
                            backgroundColor="#e5e5e5"
                            borderRadius={4}
                            overflow="hidden"
                            marginBottom={10}
                        >
                            <Pane
                                height="100%"
                                backgroundColor="#3b82f6"
                                width={`${Math.min((processed / 100000) * 100, 100)}%`}
                                transition="width 0.3s ease"
                            />
                        </Pane>

                        <Pane display="flex" justifyContent="space-between">
                            <Text size={300} color="muted">
                                Procesate: {processed.toLocaleString()}
                            </Text>
                            <Text size={300} color="muted">
                                Create: {progress.created || 0} | 
                                Actualizate: {progress.updated || 0} | 
                                Sărite: {progress.skipped || 0}
                            </Text>
                        </Pane>
                    </>
                )}
            </Pane>
        );
    }

    render() {
        const { isShown } = this.props;
        const { isSyncing, progress } = this.state;

        return (
            <Dialog
                isShown={isShown}
                title="Sincronizare Catalog HEO"
                onCloseComplete={this.handleClose}
                hasFooter={!isSyncing}
                confirmLabel={progress?.type === 'complete' ? 'Închide' : 'Începe sincronizarea'}
                onConfirm={progress?.type === 'complete' ? this.handleClose : this.startSync}
                isConfirmDisabled={isSyncing}
                cancelLabel="Anulează"
                width={500}
            >
                {this.renderProgress()}
            </Dialog>
        );
    }
}

export default HeoSyncModal;
